import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calculator, TrendingUp, Target, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import DemoForm from "@/components/forms/DemoForm";

const faqs = [
  {
    question: "¿Qué significa RevPAR?",
    answer: "RevPAR significa 'Revenue Per Available Room' o Ingreso por Habitación Disponible en español. Es una métrica que mide el rendimiento financiero de un hotel considerando tanto la ocupación como la tarifa promedio."
  },
  {
    question: "¿Cómo se calcula el RevPAR?",
    answer: "Hay dos formas de calcular el RevPAR: 1) Dividir los ingresos totales por habitaciones entre el número de habitaciones disponibles, o 2) Multiplicar la Tarifa Diaria Promedio (ADR) por el porcentaje de ocupación."
  },
  {
    question: "¿Cuál es un buen RevPAR?",
    answer: "No existe un número universal. El 'buen' RevPAR depende del mercado, tipo de hotel, ubicación y temporada. Lo importante es comparar tu RevPAR con competidores similares (índice RevPAR o RGI) y buscar mejorarlo continuamente."
  },
  {
    question: "¿Qué diferencia hay entre RevPAR y ADR?",
    answer: "El ADR (Average Daily Rate) solo considera las habitaciones vendidas, mientras que el RevPAR considera todas las habitaciones disponibles. Un hotel puede tener alto ADR pero bajo RevPAR si su ocupación es baja."
  },
  {
    question: "¿Cómo puedo mejorar mi RevPAR?",
    answer: "Puedes mejorar el RevPAR aumentando ocupación, aumentando tarifas, o ambos. Las estrategias incluyen pricing dinámico, mejor distribución de canales, marketing efectivo, y revenue management profesional."
  },
];

export default function RevparPage() {
  useEffect(() => {
    document.title = "¿Qué es RevPAR? | Guía Completa para Hoteleros - Zimoo";
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    });
    document.head.appendChild(script);
    
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="section-padding bg-gradient-to-b from-[#FAFAFA] to-white grain-overlay">
        <div className="max-w-7xl mx-auto container-padding relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <span className="label-text mb-4 block">MÉTRICA CLAVE</span>
            <h1 className="heading-1 mb-6">
              ¿Qué es el <span className="text-gradient">RevPAR</span>?
            </h1>
            <p className="body-text text-lg mb-8">
              Aprende todo sobre la métrica más importante en la industria hotelera y cómo utilizarla para maximizar los ingresos de tu hotel.
            </p>
          </div>
        </div>
      </section>

      {/* AEO Block */}
      <section className="py-12 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto container-padding text-center">
          <h2 className="text-xl font-semibold text-white mb-3">¿Qué es RevPAR en hotelería?</h2>
          <p className="text-gray-300 leading-relaxed">
            El RevPAR (Revenue Per Available Room) es la métrica fundamental que mide el rendimiento financiero de un hotel. Se calcula dividiendo los ingresos por habitaciones entre las habitaciones disponibles, o multiplicando ADR por ocupación. Un RevPAR alto indica éxito en combinar buena ocupación con tarifas óptimas.
          </p>
        </div>
      </section>

      {/* Definition & Formula */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="heading-2 mb-6">Definición de RevPAR</h2>
              <p className="body-text mb-4">
                El <strong>RevPAR</strong> (Revenue Per Available Room o Ingreso por Habitación Disponible) es la métrica más utilizada en la industria hotelera para medir el rendimiento financiero de un hotel.
              </p>
              <p className="body-text mb-4">
                A diferencia de otras métricas que solo consideran habitaciones vendidas, el RevPAR toma en cuenta TODAS las habitaciones disponibles, dando una imagen más completa del rendimiento real del hotel.
              </p>
              <p className="body-text">
                Un RevPAR alto indica que el hotel está logrando una buena combinación de ocupación y tarifa promedio, mientras que un RevPAR bajo puede indicar problemas en cualquiera de estas áreas.
              </p>
            </div>
            
            <div className="bg-[#FAFAFA] rounded-2xl p-8 border border-black/5">
              <div className="flex items-center gap-3 mb-6">
                <Calculator className="h-8 w-8 text-[#1ABCB4]" />
                <h3 className="text-xl font-semibold text-[#0A0A0A]">Fórmulas del RevPAR</h3>
              </div>
              
              <div className="space-y-6">
                <div className="bg-white rounded-xl p-4 border border-black/5">
                  <p className="text-sm text-[#404040] mb-2">Fórmula 1:</p>
                  <p className="text-lg font-mono font-semibold text-[#0A0A0A]">
                    RevPAR = Ingresos por Habitaciones / Habitaciones Disponibles
                  </p>
                </div>
                
                <div className="bg-white rounded-xl p-4 border border-black/5">
                  <p className="text-sm text-[#404040] mb-2">Fórmula 2:</p>
                  <p className="text-lg font-mono font-semibold text-[#0A0A0A]">
                    RevPAR = ADR × Ocupación
                  </p>
                </div>
                
                <p className="text-sm text-[#404040]">
                  Donde ADR = Average Daily Rate (Tarifa Diaria Promedio)
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Example */}
      <section className="section-padding bg-[#FAFAFA]">
        <div className="max-w-4xl mx-auto container-padding">
          <h2 className="heading-2 mb-8 text-center">Ejemplo Práctico</h2>
          
          <div className="bg-white rounded-2xl p-8 border border-black/5 shadow-sm">
            <h3 className="text-lg font-semibold text-[#0A0A0A] mb-6">Hotel con 100 habitaciones</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="text-center p-4 bg-[#FAFAFA] rounded-xl">
                <p className="text-3xl font-bold text-[#0A0A0A]">$12,000</p>
                <p className="text-sm text-[#404040]">Ingresos del día</p>
              </div>
              <div className="text-center p-4 bg-[#FAFAFA] rounded-xl">
                <p className="text-3xl font-bold text-[#0A0A0A]">80%</p>
                <p className="text-sm text-[#404040]">Ocupación</p>
              </div>
              <div className="text-center p-4 bg-[#FAFAFA] rounded-xl">
                <p className="text-3xl font-bold text-[#0A0A0A]">$150</p>
                <p className="text-sm text-[#404040]">ADR</p>
              </div>
            </div>
            
            <div className="border-t border-black/5 pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-[#404040] mb-2">Cálculo con Fórmula 1:</p>
                  <p className="font-mono text-[#0A0A0A]">RevPAR = $12,000 / 100 = <span className="text-[#1ABCB4] font-bold">$120</span></p>
                </div>
                <div>
                  <p className="text-sm text-[#404040] mb-2">Cálculo con Fórmula 2:</p>
                  <p className="font-mono text-[#0A0A0A]">RevPAR = $150 × 0.80 = <span className="text-[#1ABCB4] font-bold">$120</span></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why RevPAR Matters */}
      <section className="section-padding bg-white">
        <div className="max-w-4xl mx-auto container-padding">
          <h2 className="heading-2 mb-8 text-center">¿Por qué es Importante el RevPAR?</h2>
          
          <div className="space-y-6">
            {[
              {
                icon: TrendingUp,
                title: "Visión Completa del Rendimiento",
                desc: "El RevPAR combina ocupación y tarifa en una sola métrica, mostrando el verdadero rendimiento del hotel. Un ADR alto con baja ocupación puede resultar en bajo RevPAR."
              },
              {
                icon: Target,
                title: "Benchmarking Efectivo",
                desc: "Permite comparar tu rendimiento con competidores de manera justa, ya que considera el inventario total disponible."
              },
              {
                icon: Calculator,
                title: "Base para Decisiones",
                desc: "Es la métrica principal para evaluar estrategias de pricing y distribución. Cualquier cambio debe evaluarse por su impacto en RevPAR."
              },
            ].map((item, index) => (
              <div key={index} className="flex gap-4 p-6 bg-[#FAFAFA] rounded-xl">
                <item.icon className="h-8 w-8 text-[#1ABCB4] flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-[#0A0A0A] mb-2">{item.title}</h3>
                  <p className="text-[#404040]">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Improve */}
      <section className="section-padding bg-[#FAFAFA]">
        <div className="max-w-4xl mx-auto container-padding">
          <h2 className="heading-2 mb-8 text-center">Cómo Mejorar tu RevPAR</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl p-6 border border-black/5">
              <h3 className="font-semibold text-[#0A0A0A] mb-4">Aumentar Ocupación</h3>
              <ul className="space-y-3">
                {[
                  "Optimizar presencia en OTAs",
                  "Mejorar marketing digital",
                  "Diversificar segmentos de clientes",
                  "Ofrecer promociones estratégicas",
                  "Mejorar reputación online"
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-[#404040] text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-white rounded-xl p-6 border border-black/5">
              <h3 className="font-semibold text-[#0A0A0A] mb-4">Aumentar ADR</h3>
              <ul className="space-y-3">
                {[
                  "Implementar pricing dinámico",
                  "Segmentar clientes por valor",
                  "Ofrecer upgrades y upsells",
                  "Mejorar percepción de valor",
                  "Gestionar restricciones de estancia"
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-[#404040] text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          <div className="mt-8 text-center">
            <Link to="/pricing-dinamico">
              <Button className="btn-primary">
                Aprende sobre Pricing Dinámico
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Related Metrics */}
      <section className="section-padding bg-white">
        <div className="max-w-4xl mx-auto container-padding">
          <h2 className="heading-2 mb-8 text-center">Métricas Relacionadas</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-6 border border-black/5 rounded-xl">
              <h3 className="font-semibold text-[#0A0A0A] mb-2">ADR</h3>
              <p className="text-sm text-[#404040] mb-2">Average Daily Rate</p>
              <p className="text-xs text-[#404040]">Tarifa promedio de habitaciones vendidas. ADR = Ingresos / Habitaciones Vendidas</p>
            </div>
            <div className="p-6 border border-black/5 rounded-xl">
              <h3 className="font-semibold text-[#0A0A0A] mb-2">TRevPAR</h3>
              <p className="text-sm text-[#404040] mb-2">Total Revenue Per Available Room</p>
              <p className="text-xs text-[#404040]">Incluye todos los ingresos del hotel, no solo habitaciones.</p>
            </div>
            <div className="p-6 border border-black/5 rounded-xl">
              <h3 className="font-semibold text-[#0A0A0A] mb-2">RGI</h3>
              <p className="text-sm text-[#404040] mb-2">Revenue Generation Index</p>
              <p className="text-xs text-[#404040]">Compara tu RevPAR vs el mercado. RGI = Tu RevPAR / RevPAR del Mercado</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="section-padding bg-[#FAFAFA]">
        <div className="max-w-4xl mx-auto container-padding">
          <h2 className="heading-2 mb-8 text-center">Preguntas Frecuentes sobre RevPAR</h2>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left text-[#0A0A0A] font-medium">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-[#404040]">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="label-text mb-4 block">MEJORA TU REVPAR</span>
              <h2 className="heading-2 mb-6">
                Optimiza el RevPAR de tu Hotel
              </h2>
              <p className="body-text mb-6">
                Zimoo te ayuda a maximizar tu RevPAR con pricing dinámico automatizado y forecasting inteligente. Solicita una demo para ver cómo podemos aumentar tus ingresos.
              </p>
              <Link to="/revenue-management-hoteles" className="inline-flex items-center text-[#1ABCB4] hover:underline font-medium">
                Aprende sobre Revenue Management <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </div>
            <DemoForm sourcePage="revpar" />
          </div>
        </div>
      </section>
    </div>
  );
}
